import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Modal, TextField, Typography } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";

import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { ProductInterface } from "../../common/interfaces";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import PaginationControls from "../PaginationControls";
import PageToaster from "../toaster/PageToaster";
import { EmptyList, TaskRow, TaskRowList } from "./TaskListModal.styled";

interface ProductLowStockModalProps {
  open: boolean;
  onClose: () => void;
}

const PAGE_SIZE = 8;

/**
 * Produtos com estoque igual/abaixo da quantidade mínima cadastrada, entre
 * os que têm o alerta ligado. Somente leitura — a edição continua no
 * cadastro de produto.
 */
const ProductLowStockModal: React.FC<ProductLowStockModalProps> = ({
  open,
  onClose
}) => {
  const [products, setProducts] = useState<ProductInterface[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const containerId = "toaster-product-low-stock-modal";
  const { autoError } = usePageToast(containerId, true);
  const handleHttpError = makeToasterErrorHandler(autoError);

  const fetchProducts = useCallback(
    async (currentPage: number, currentSearch: string) => {
      setLoading(true);
      try {
        const response = await api.get(`/products/low-stock`, {
          params: {
            page: currentPage,
            limit: PAGE_SIZE,
            search: currentSearch.trim() || undefined
          }
        });

        setProducts(response.data.items);
        setTotalPages(response.data.meta.totalPages || 1);
      } catch (error) {
        console.error("Erro ao buscar produtos com estoque baixo:", error);
        handleHttpError(error);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (!open) return;
    fetchProducts(page, search);
  }, [open, page, search, fetchProducts]);

  useEffect(() => {
    if (open) {
      setPage(1);
      setSearch("");
    }
  }, [open]);

  return (
    <>
      <PageToaster containerId={containerId} />
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="modal-product-low-stock"
      >
        <Box
          sx={{
            position: "absolute" as const,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            border: "1px solid var(--border-strong)",
            p: 4,
            borderRadius: 2,
            boxShadow: 24,
            width: { xs: "92vw", sm: "680px" },
            maxHeight: "90vh",
            overflowY: "auto"
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Produtos com Estoque Baixo</Typography>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <TextField
            size="small"
            fullWidth
            label="Buscar por nome ou código de barras"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            sx={{ mb: 2 }}
          />

          <TaskRowList>
            {products.map((product) => (
              <TaskRow key={product.product_id} $accent="#fb7185">
                <h3>{product.product_name}</h3>
                <div className="meta">
                  <span>Estoque atual: {product.product_quantity}</span>
                  <span>Mínimo: {product.product_minimum_quantity}</span>
                  {product.product_barcode && (
                    <span>Código de barras: {product.product_barcode}</span>
                  )}
                </div>
              </TaskRow>
            ))}

            {!loading && products.length === 0 && (
              <EmptyList>
                Nenhum produto abaixo do mínimo. Estoque em dia!
              </EmptyList>
            )}
          </TaskRowList>

          {totalPages > 1 && (
            <PaginationControls
              page={page}
              totalPages={totalPages}
              setPage={setPage}
            />
          )}
        </Box>
      </Modal>
    </>
  );
};

export default ProductLowStockModal;
